/**
 * Appearance controls.
 *
 * Everything here is a personal setting saved against the signed-in employee, not
 * a company-wide one — colleagues keep whatever they chose. The chart palette is
 * deliberately excluded: it is validated for colour-blind readability, and letting
 * each person re-tint it would break shared screenshots and printed reports.
 */
import { useTheme, THEMES, MODES, DENSITIES, NAV_STYLES, swatchFor } from '../lib/theme.jsx';
import { Icon } from '../lib/icons.jsx';

function ThemeSwatch({ theme, dark }) {
  const ramp = (k) => swatchFor(theme, dark, k);
  return (
    <div className="theme-swatch">
      <i className="sw-main" style={{ background: ramp(500) }} />
      <i style={{ background: ramp(300) }} />
      <i style={{ background: ramp(700) }} />
      <i style={{ background: ramp(100) }} />
    </div>
  );
}

export function ThemeCards() {
  const { appearance, update } = useTheme();
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';

  return (
    <div className="theme-grid">
      {THEMES.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`theme-card${appearance.accent === t.id ? ' on' : ''}`}
          style={{ '--tc-500': swatchFor(t, dark), '--tc-ring': `hsl(${t.h} ${t.s}% 50% / .3)`, textAlign: 'left' }}
          onClick={() => update({ accent: t.id })}
        >
          <div className="tc-check"><Icon name="check" strokeWidth={3.4} /></div>
          <ThemeSwatch theme={t} dark={dark} />
          <div className="tc-name">
            {t.name}
            {t.tag && <span className="badge" style={{ fontSize: '9.6px', marginLeft: 6 }}>{t.tag}</span>}
          </div>
          <div className="tc-desc">{t.desc}</div>
        </button>
      ))}
    </div>
  );
}

export function ThemePreview() {
  return (
    <div className="preview-shell">
      <div className="preview-top">
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--brand-500)' }} />
        <span style={{ height: 7, width: 74, borderRadius: 4, background: 'var(--surface-3)' }} />
        <span style={{ marginLeft: 'auto' }} className="preview-btn">Action</span>
      </div>
      <div className="preview-body">
        <div className="preview-nav">
          <i className="on" /><i /><i /><i /><i />
        </div>
        <div className="preview-main">
          <div className="preview-card">
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-3)', letterSpacing: '.06em' }}>PRESENT TODAY</div>
            <div style={{ fontSize: 17, fontWeight: 750, letterSpacing: '-.03em' }}>
              157<span style={{ fontSize: 10, color: 'var(--text-3)' }}> / 181</span>
            </div>
            <div className="preview-bar" style={{ width: '78%' }} />
          </div>
          <div className="preview-card" style={{ marginBottom: 0 }}>
            <div style={{ height: 6, width: '58%', borderRadius: 3, background: 'var(--surface-3)', marginBottom: 5 }} />
            <div style={{ height: 6, width: '82%', borderRadius: 3, background: 'var(--surface-3)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

const MODE_ICON = { light: 'sun', dark: 'moon', system: 'cpu' };

export default function AppearancePanel({ name }) {
  const { appearance, update } = useTheme();

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 13.4, marginBottom: 4 }}>Accent colour</div>
      <div className="txt-sm muted" style={{ marginBottom: 11 }}>
        Charts keep a fixed, colour-blind-safe palette whichever accent you pick — only the interface changes.
      </div>
      <ThemeCards />

      <div className="divider" />

      <div className="grid g-2" style={{ gap: 18 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.4, marginBottom: 8 }}>Appearance mode</div>
          <div className="opt-row">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`opt${appearance.mode === m.id ? ' on' : ''}`}
                style={{ textAlign: 'left' }}
                onClick={() => update({ mode: m.id })}
              >
                <div className="on-t"><Icon name={MODE_ICON[m.id]} />{m.name}</div>
              </button>
            ))}
          </div>

          <div style={{ fontWeight: 700, fontSize: 13.4, margin: '16px 0 8px' }}>Density</div>
          <div className="opt-row">
            {DENSITIES.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`opt${appearance.density === d.id ? ' on' : ''}`}
                style={{ textAlign: 'left' }}
                onClick={() => update({ density: d.id })}
              >
                <div className="on-t"><Icon name={d.id === 'compact' ? 'rows' : 'grid'} />{d.name}</div>
                <div className="on-d">{d.desc}</div>
              </button>
            ))}
          </div>

          <div style={{ fontWeight: 700, fontSize: 13.4, margin: '16px 0 8px' }}>Sidebar</div>
          <div className="opt-row">
            {NAV_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`opt${appearance.navStyle === s.id ? ' on' : ''}`}
                style={{ textAlign: 'left' }}
                onClick={() => update({ navStyle: s.id })}
              >
                <div className="on-t"><Icon name="layout" />{s.name}</div>
                <div className="on-d">{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontWeight: 700, fontSize: 13.4, marginBottom: 8 }}>Preview</div>
          <ThemePreview />
          <div className="ai-card" style={{ marginTop: 14 }}>
            <span className="ai-tag"><Icon name="user" />Personal setting</span>
            <div className="txt-sm" style={{ color: 'var(--text-2)', marginTop: 9 }}>
              This is saved against <b>{name}</b> only. Colleagues keep whatever they chose, and HR cannot
              override it — appearance is not something an employer needs to control.
            </div>
          </div>
          <div className="txt-sm muted mt-14">
            <Icon name="eye" /> Every accent is checked for text contrast at runtime, so button labels stay
            readable on light and dark surfaces alike.
          </div>
        </div>
      </div>
    </div>
  );
}
